import { createUnsafeLinkPath } from './createLinkPath';

interface Options {
    currentRoute: string;
    route: string;
}

/**
 * Determines if a given route is active based on the current route.
 * A route is considered active if the current route starts with the given route.
 *
 * @param {Options} options - The options object
 * @param {string} options.route - The route to check for activity
 * @param {string} options.currentRoute - The current active route
 * @returns {boolean} True if the route is active, false otherwise
 *
 * @example
 * ```typescript
 *
 * console.log(isActiveRoute({ route: '/users', currentRoute: '/users/profile' }));
 * // Output: true
 *
 * console.log(isActiveRoute({ route: '/settings', currentRoute: '/users/profile' }));
 * // Output: false
 *
 * // Can be used in a navigation component
 * const NavLink = ({ to, children }) => {
 *   const currentRoute = useCurrentRoute();
 *   const isActive = isActiveRoute({ route: to, currentRoute });
 *
 *   return (
 *     <a href={to} className={isActive ? 'active' : ''}>
 *       {children}
 *     </a>
 *   );
 * };
 * ```
 *
 * @remarks
 * This function is useful for:
 * - Determining which navigation items should be highlighted
 * - Conditional rendering based on the active route
 */
export function isActiveRoute({ route, currentRoute }: Options): boolean {
    return createUnsafeLinkPath(currentRoute).startsWith(createUnsafeLinkPath(route));
}
